/**
 * Source Toggle Button Component
 * Button to show/hide message sources
 */

import '../../../../styles/oc-utilities.css';

import React, { useCallback } from 'react';

import { ChevronIcon, ToggleButton } from './SourceToggleButton.styled';

export type SourceToggleButtonProps = {
  messageId: string;
  sourceCount: number;
  isExpanded: boolean;
  onToggle: (messageId: string) => void;
  ariaControlsId: string;
  className?: string;
};

/**
 * SourceToggleButton Component
 * Displays a button to toggle the visibility of message sources
 */
const SourceToggleButton: React.FC<SourceToggleButtonProps> = ({
  messageId,
  sourceCount,
  isExpanded,
  onToggle,
  ariaControlsId,
  className = '',
}) => {
  const handleClick = useCallback(() => {
    onToggle(messageId);
  }, [messageId, onToggle]);

  const buttonText = `${sourceCount} Source${sourceCount !== 1 ? 's' : ''}`;
  const ariaLabel = `${isExpanded ? 'Hide' : 'Show'} ${buttonText}`;

  return (
    <ToggleButton
      type="button"
      onClick={handleClick}
      className={className}
      aria-expanded={isExpanded}
      aria-controls={ariaControlsId}
      aria-label={ariaLabel}
      data-qa-id="toggle-message-sources"
    >
      {/* Chevron Icon */}
      <ChevronIcon
        $isExpanded={isExpanded}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 18l6-6-6-6" />
      </ChevronIcon>
      <span>{buttonText}</span>
    </ToggleButton>
  );
};

export default React.memo(SourceToggleButton);
